import { Fragment } from 'react';
import { Box, Card, Heading, List, Paragraph, Text, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';
import { ApplicationConfigurationCustomQuestion } from '@zg-rentals/ts-capps-client';
import { Controller, useWatch } from 'react-hook-form';
import type { Answers } from '../../../../../models/rental-application';
import { useApplicationContext } from '../../../../../contexts/ApplicationContext';
import { useIsCustomQuestionEnabled } from '../../../../../hooks/useIsCustomQuestionEnabled';
import { STEP_IDS } from '../../../../../constants/wizard';
import {
  getCustomQuestionDocuments,
  getHasPets,
  getPetSummaryParts,
  getPetSummarySeparator,
} from '../../../../../utils/customQuestions';
import type { SupplementalSectionProps } from '../supplementalSections';
import { EditInStepLink } from '../EditInStepLink';
import { CustomQuestionDocumentsField } from './CustomQuestionDocumentsField';

const SERVICE_ANIMAL_QUESTION = 'Do you have a service animal or emotional support animal (ESA)?';

function ServiceAnimalQuestion({ applicationId, savedAnswers }: { applicationId: string; savedAnswers: Answers }) {
  const hasServiceAnimal = useWatch({ name: 'hasServiceAnimal' }) as Answers['hasServiceAnimal'];

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Controller
        name="hasServiceAnimal"
        render={({ field: { onChange, value, onBlur } }) => (
          <Card
            elevated={false}
            outlined
            css={{
              display: 'flex',
              flexDirection: { base: 'column', md: 'row' },
              alignItems: { base: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 'default',
            }}
          >
            <Text textStyle="body-sm-bold">{SERVICE_ANIMAL_QUESTION}</Text>
            <ToggleButtonGroup
              aria-label={SERVICE_ANIMAL_QUESTION}
              conjoined
              value={value === true ? 'yes' : value === false ? 'no' : ''}
              onValueChange={(next) => {
                if (next === 'yes' || next === 'no') {
                  onChange(next === 'yes');
                }
              }}
              onBlur={onBlur}
              css={{ flexShrink: 0, minWidth: { md: '208px' } }}
              fluid
            >
              <ToggleButton value="yes">Yes</ToggleButton>
              <ToggleButton value="no">No</ToggleButton>
            </ToggleButtonGroup>
          </Card>
        )}
      />
      {hasServiceAnimal === true && (
        <CustomQuestionDocumentsField
          applicationId={applicationId}
          question={ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD}
          heading="Upload your animal’s certification"
          savedLabel="certifications"
          documentLabel="certification"
          description={
            <Paragraph>Attach documentation from a licensed professional confirming your animal’s status.</Paragraph>
          }
          documents={getCustomQuestionDocuments(
            savedAnswers,
            ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD,
          )}
        />
      )}
    </Box>
  );
}

export function PetRecordsSection({ applicationId, answers, onPersist }: SupplementalSectionProps) {
  const { application } = useApplicationContext();
  const isVeterinaryRecordsEnabled = useIsCustomQuestionEnabled(
    ApplicationConfigurationCustomQuestion.VETERINARY_RECORDS_UPLOAD,
  );
  const isPetPhotoEnabled = useIsCustomQuestionEnabled(ApplicationConfigurationCustomQuestion.PET_PHOTO_UPLOAD);
  const isServiceAnimalEnabled = useIsCustomQuestionEnabled(
    ApplicationConfigurationCustomQuestion.ESA_CERTIFICATION_UPLOAD,
  );
  const savedAnswers = application.answers ?? {};
  const petSummaryParts = getPetSummaryParts(answers);
  const hasPets = getHasPets(answers);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      {hasPets && (
        <Box>
          <Heading level={4} css={{ textStyle: 'body-bold', color: 'text.subtle' }}>
            Pet information
          </Heading>
          <Paragraph>
            We see{' '}
            {petSummaryParts.map((part, index) => (
              <Fragment key={part}>
                {getPetSummarySeparator(index, petSummaryParts.length)}
                <strong>{part}</strong>
              </Fragment>
            ))}{' '}
            listed.{' '}
            <EditInStepLink
              applicationId={applicationId}
              stepId={STEP_IDS.PETS}
              label="Household"
              onPersist={onPersist}
            />
          </Paragraph>
        </Box>
      )}
      {hasPets && isVeterinaryRecordsEnabled && (
        <CustomQuestionDocumentsField
          applicationId={applicationId}
          question={ApplicationConfigurationCustomQuestion.VETERINARY_RECORDS_UPLOAD}
          heading="Upload records for each pet below"
          savedLabel="veterinary records"
          documentLabel="veterinary record"
          description={
            <Box>
              <Paragraph>Include one or more of the following per pet:</Paragraph>
              <List.Root css={{ marginBlock: 'none' }}>
                <List.Item css={{ paddingBlock: 'none' }}>Most recent vet visit summary</List.Item>
                <List.Item css={{ paddingBlock: 'none' }}>Up-to-date vaccination records</List.Item>
                <List.Item css={{ paddingBlock: 'none' }}>Health certificate</List.Item>
              </List.Root>
            </Box>
          }
          documents={getCustomQuestionDocuments(
            savedAnswers,
            ApplicationConfigurationCustomQuestion.VETERINARY_RECORDS_UPLOAD,
          )}
        />
      )}
      {hasPets && isPetPhotoEnabled && (
        <CustomQuestionDocumentsField
          applicationId={applicationId}
          question={ApplicationConfigurationCustomQuestion.PET_PHOTO_UPLOAD}
          heading="Upload a photo of each pet"
          savedLabel="pet photos"
          documentLabel="pet photo"
          documents={getCustomQuestionDocuments(savedAnswers, ApplicationConfigurationCustomQuestion.PET_PHOTO_UPLOAD)}
        />
      )}
      {isServiceAnimalEnabled && <ServiceAnimalQuestion applicationId={applicationId} savedAnswers={savedAnswers} />}
    </Box>
  );
}
